import { setupSnapshotKey } from './setup-snapshot';

/**
 * Job setup edits are a draft until Save (2026-10-04).
 *
 * Job setup says "Unsaved changes · Discard · Save", but every edit it made went
 * through the shell's job update, which saves the whole job to the engine 450 ms
 * later. Dragging the Document type bar from 70 to 55 without saving was therefore
 * saved: the engine's held-work re-judge ran under the dragged bar and released 14
 * documents, clicking Autopilot released 3 more, and Discard then saved the old
 * setup back over them. The documents stayed changed.
 *
 * So the setup editor of a job in use keeps its draft here, in the renderer, and
 * only Save commits it. The engine, the work screen behind setup and any wave read
 * meanwhile all keep using the saved job; the Review thresholds screen prices the
 * draft through the engine's read-only preview (DocumentStore.PreviewReviewRelease).
 * A first setup (a provisional job, before it has work) keeps its old path.
 */
export type DraftableJob = { id: string; status?: string | null };

export type SetupDrafts<T> = Readonly<Record<string, T>>;

/** Whether a Job setup change in this mode is held as a draft rather than saved. */
export const holdsSetupDraft = (mode: string, saved: DraftableJob | null | undefined): boolean =>
  mode === 'setup' && saved?.status === 'active';

/** The drafts after a setup change; a draft identical to the saved job is no draft (Discard). */
export function draftAfterChange<T extends DraftableJob>(
  drafts: SetupDrafts<T>,
  saved: T,
  next: T,
): SetupDrafts<T> {
  const rest: Record<string, T> = { ...drafts };
  delete rest[next.id];
  return setupSnapshotKey(next) === setupSnapshotKey(saved) ? rest : { ...rest, [next.id]: next };
}

/** What Job setup shows: its draft while it is open, otherwise the saved job. */
export const jobWithSetupDraft = <T extends DraftableJob>(
  drafts: SetupDrafts<T>,
  saved: T,
  mode: string,
): T => (holdsSetupDraft(mode, saved) ? (drafts[saved.id] ?? saved) : saved);

/** Leaving setup (or the job) discards what was never saved. */
export function draftsStillOpen<T extends DraftableJob>(
  drafts: SetupDrafts<T>,
  saved: DraftableJob | null | undefined,
  mode: string,
): SetupDrafts<T> {
  const keep = saved && holdsSetupDraft(mode, saved) ? saved.id : null;
  const ids = Object.keys(drafts);
  if (ids.every((id) => id === keep)) return drafts;
  return keep && drafts[keep] ? { [keep]: drafts[keep] } : {};
}

/** The drafts once a job's setup is durably saved (Save, or a change the engine made itself). */
export function draftsWithout<T extends DraftableJob>(
  drafts: SetupDrafts<T>,
  jobId: string,
): SetupDrafts<T> {
  if (!(jobId in drafts)) return drafts;
  const rest: Record<string, T> = { ...drafts };
  delete rest[jobId];
  return rest;
}
